import { AlertCircle, RefreshCw } from 'lucide-react';
import React from 'react';
import { Button } from '@/components/ui/button';
import { createLogger } from '@/utils/logger';
import { captureFrontendException } from '@/utils/sentry';

const log = createLogger('ErrorBoundary');

export type FallbackProps = {
  message?: string;
  reset: () => void;
};

export const FormErrorFallback = ({ message, reset }: FallbackProps) => (
  <div className="w-full flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
    <div className="flex items-center gap-2 min-w-0">
      <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />
      <p className="text-sm text-red-700 truncate">
        {message ?? "Something didn't work as expected. Please try again."}
      </p>
    </div>
    <Button
      variant="outline"
      size="sm"
      onClick={reset}
      className="shrink-0 gap-1.5 border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800"
    >
      <RefreshCw className="h-3.5 w-3.5" />
      Try again
    </Button>
  </div>
);

type FallbackRenderer = (props: FallbackProps) => React.ReactNode;

type ErrorBoundaryProps = {
  children: React.ReactNode;
  fallback?: React.ReactNode | FallbackRenderer;
  onReset?: () => void;
};

type ErrorBoundaryState = {
  hasError: boolean;
  message?: string;
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    const message =
      error instanceof Error
        ? error.message
        : typeof error === 'string'
          ? error
          : "Something didn't work as expected";
    return { hasError: true, message };
  }

  componentDidCatch(error: unknown, errorInfo: unknown) {
    log.error('Caught an unhandled component error', { error, errorInfo });
    captureFrontendException(error, { errorInfo });
  }

  handleReset = () => {
    this.props.onReset?.();
    this.setState({ hasError: false, message: undefined });
  };

  render() {
    if (this.state.hasError) {
      const { fallback } = this.props;
      if (typeof fallback === 'function') {
        return fallback({ message: this.state.message, reset: this.handleReset });
      }
      if (fallback) return fallback;
      return <FormErrorFallback message={this.state.message} reset={this.handleReset} />;
    }
    return this.props.children;
  }
}

export const withErrorBoundary =
  <P extends object>(
    Component: React.ComponentType<P>,
    fallback?: React.ReactNode | FallbackRenderer
  ) =>
  (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );
